import { formatActivityDate } from '@/dsm-components';
import { PalmettoIcon } from '@palmetto/dsm-components';

export interface EntityHistoryChange {
  from: unknown;
  to: unknown;
}

export interface EntityHistoryEntry {
  changedBy: string;
  /** Enriched display name resolved server-side. */
  changedByName?: string;
  changedAt: string;
  changes: Record<string, EntityHistoryChange>;
  note?: string;
}

export interface EntityHistoryFeedProps {
  history: EntityHistoryEntry[];
  fieldLabel: (field: string) => string;
  formatValue: (field: string, value: unknown) => string;
}

interface HistoryEntryProps {
  entry: EntityHistoryEntry;
  fieldLabel: (field: string) => string;
  formatValue: (field: string, value: unknown) => string;
}

const HistoryEntry = ({ entry, fieldLabel, formatValue }: HistoryEntryProps) => {
  const who = entry.changedByName ?? entry.changedBy;
  const when = entry.changedAt ? formatActivityDate(entry.changedAt) : '';
  const fields = Object.entries(entry.changes ?? {});

  return (
    <div className="flex flex-col gap-4 rounded-[6px] bg-canvas-highlight-default p-16">
      <div className="body-200 text-text-tertiary">
        {when}
        {who ? ' by ' : ''}
        {who && <span className="body-bold-200 text-text-primary">{who}</span>}
      </div>
      {entry.note && <div className="body-200 text-text-secondary">&quot;{entry.note}&quot;</div>}
      <div className="flex flex-col gap-8">
        {fields.length === 0 ? (
          <div className="body-300 text-text-tertiary">No field changes recorded.</div>
        ) : (
          fields.map(([field, change]) => (
            <div key={field} className="flex flex-row flex-wrap items-center gap-4 body-300 text-text-primary">
              <span className="body-bold-300">{fieldLabel(field)}</span>
              <span className="text-text-secondary">{formatValue(field, change.from)}</span>
              <PalmettoIcon name="arrowRight" size="sm" />
              <span className="text-text-secondary">{formatValue(field, change.to)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/**
 * Newest-first feed of an entity's stored change history. The vocabulary is injected because the
 * field names are the entity's own; the rendering is not.
 */
export const EntityHistoryFeed = ({ history, fieldLabel, formatValue }: EntityHistoryFeedProps) => {
  const entries = [...(history ?? [])].toReversed();

  if (entries.length === 0) {
    return <div className="body-200 text-text-tertiary">No changes recorded yet.</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      {entries.map((entry, index) => (
        <HistoryEntry
          key={`${entry.changedAt}-${index}`}
          entry={entry}
          fieldLabel={fieldLabel}
          formatValue={formatValue}
        />
      ))}
    </div>
  );
};
